// src/components/AnonymizationProfilesPage/AnonymizationProfileFormDialog.jsx
import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Alert,
    Typography,
    Divider,
    Checkbox,
    FormControlLabel,
    TextField,
} from '@mui/material';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import GppMaybeOutlinedIcon from '@mui/icons-material/GppMaybeOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { useTranslation } from 'react-i18next';

import ProfileBasicsSection from './ProfileBasicsSection';
import CustomWordsSection from './CustomWordsSection';
import SaveToDbSection from './SaveToDbSection';
import DetectorFamilySection from './DetectorFamilySection';
import {
    GDPR_GROUPS,
    NIS2_GROUPS,
    VYHRADENE_GROUPS,
} from './detectorCatalog';

const DEFAULT_OPTIONS = {
    save_to_db: false,
    anonymize_birth_number: false,
    anonymize_names: false,
    exclude_contract_info_first_page: false,
    name_confidence_threshold: 60,
    anonymize_birth_date: false,
    anonymize_signature: false,
    anonymize_account_number: false,
    anonymize_address: false,
    anonymize_phone: false,
    anonymize_email: false,
    anonymize_passport_number: false,
    anonymize_id_number: false,
    text_anonymization_mode: 'random',
    signature_anonymization_mode: 'black_box',
    nis2_detectors: [],
    vyhradene_detectors: [],
    custom_words: [],
};

const AnonymizationProfileFormDialog = ({
    open,
    onClose,
    onSave,
    initialData,
    processType,
}) => {
    const { t } = useTranslation();
    const isEdit = !!initialData;

    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [options, setOptions] = useState(DEFAULT_OPTIONS);

    const [newCustomWord, setNewCustomWord] = useState('');
    const [error, setError] = useState('');

    useEffect(() => {
        if (open) {
            if (initialData) {
                setName(initialData.name || '');
                setDescription(initialData.description || '');
                setOptions({ ...DEFAULT_OPTIONS, ...(initialData.options || {}) });
            } else {
                setName('');
                setDescription('');
                setOptions({ ...DEFAULT_OPTIONS });
            }
            setNewCustomWord('');
            setError('');
        }
    }, [open, initialData]);

    const updateOption = (key, value) => {
        setOptions((prev) => ({ ...prev, [key]: value }));
    };

    // --- Jednotné handlery pre DetectorFamilySection ---
    // GDPR = boolean flagy v options[key]
    const flagChecked = (key) => !!options[key];
    const flagToggle = (key) => updateOption(key, !options[key]);
    const flagSetMany = (keys, value) =>
        setOptions((prev) => {
            const next = { ...prev };
            keys.forEach((k) => { next[k] = value; });
            return next;
        });

    // NIS2 / VYHRADENÉ = pole kľúčov v options[arrKey]
    const arrChecked = (arrKey) => (key) => (options[arrKey] || []).includes(key);
    const arrToggle = (arrKey) => (key) =>
        setOptions((prev) => {
            const cur = new Set(prev[arrKey] || []);
            if (cur.has(key)) cur.delete(key); else cur.add(key);
            return { ...prev, [arrKey]: Array.from(cur) };
        });
    const arrSetMany = (arrKey) => (keys, value) =>
        setOptions((prev) => {
            const cur = new Set(prev[arrKey] || []);
            keys.forEach((k) => (value ? cur.add(k) : cur.delete(k)));
            return { ...prev, [arrKey]: Array.from(cur) };
        });

    const handleSubmit = () => {
        if (!name.trim()) {
            setError(t('anonymizationProfiles.errorNameRequired', 'Profile name is required.'));
            return;
        }
        onSave({ name: name.trim(), description: description.trim(), options });
    };

    // Pod-voľby pre Mená (zobrazené keď je detektor mien zapnutý)
    const gdprFooter = options.anonymize_names ? (
        <Box sx={{ mt: 2, pt: 2, borderTop: '1px dashed', borderColor: 'divider' }}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                Spresnenie pre Mená a priezviská
            </Typography>
            <FormControlLabel
                sx={{ display: 'flex', mt: 0.5 }}
                control={
                    <Checkbox
                        size="small"
                        checked={!!options.exclude_contract_info_first_page}
                        onChange={(e) => updateOption('exclude_contract_info_first_page', e.target.checked)}
                    />
                }
                label={
                    <Typography variant="body2" color="text.secondary">
                        {t('anonymizationProfiles.exclude_contract_info', 'Vynechať hlavičku (zmluvné strany)')}
                    </Typography>
                }
            />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 0.5 }}>
                <Typography variant="body2" color="text.secondary">
                    {t('anonymizationProfiles.confidence_label', 'Istota:')}
                </Typography>
                <TextField
                    type="number"
                    size="small"
                    value={options.name_confidence_threshold ?? ''}
                    onChange={(e) => {
                        let val = parseInt(e.target.value, 10);
                        if (isNaN(val)) val = 0;
                        updateOption('name_confidence_threshold', val);
                    }}
                    inputProps={{ min: 0, max: 100, step: 5 }}
                    sx={{ width: 90 }}
                />
                <Typography variant="body2" color="text.secondary">%</Typography>
            </Box>
        </Box>
    ) : null;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{ sx: { borderRadius: 3, backgroundImage: 'none' } }}
        >
            <DialogTitle sx={{ bgcolor: 'background.default', pb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {isEdit
                        ? t('anonymizationProfiles.dialogEditTitle', 'Upraviť profil')
                        : t('anonymizationProfiles.dialogCreateTitle', 'Nový profil')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {t('anonymizationProfiles.dialogSubtitle', 'Vyberte detektory, ktoré tento profil použije. (i) pri každom detektore vysvetľuje, čo deteguje.')}
                </Typography>
            </DialogTitle>

            <Divider />

            <DialogContent dividers sx={{ p: { xs: 2, md: 3 }, borderBottom: 'none' }}>
                {error && (
                    <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                        {error}
                    </Alert>
                )}

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <ProfileBasicsSection
                        name={name}
                        description={description}
                        onNameChange={setName}
                        onDescriptionChange={setDescription}
                    />

                    <DetectorFamilySection
                        title="GDPR – osobné údaje"
                        subtitle="Osobné údaje fyzických osôb podľa GDPR / zák. 18/2018 Z. z."
                        Icon={BadgeOutlinedIcon}
                        groups={GDPR_GROUPS}
                        isChecked={flagChecked}
                        onToggle={flagToggle}
                        onSetMany={flagSetMany}
                        footer={gdprFooter}
                    />

                    <DetectorFamilySection
                        title="NIS2 – kybernetické a technické údaje"
                        subtitle="Sieťové identifikátory, prístupové údaje, technické a cloud parametre (NIS2 / 366/2024 Z. z.)."
                        Icon={GppMaybeOutlinedIcon}
                        groups={NIS2_GROUPS}
                        isChecked={arrChecked('nis2_detectors')}
                        onToggle={arrToggle('nis2_detectors')}
                        onSetMany={arrSetMany('nis2_detectors')}
                    />

                    <DetectorFamilySection
                        title="VYHRADENÉ – utajované skutočnosti"
                        subtitle="Detektory utajovaných skutočností podľa zák. 215/2004 Z. z."
                        Icon={LockOutlinedIcon}
                        groups={VYHRADENE_GROUPS}
                        isChecked={arrChecked('vyhradene_detectors')}
                        onToggle={arrToggle('vyhradene_detectors')}
                        onSetMany={arrSetMany('vyhradene_detectors')}
                    />

                    <SaveToDbSection
                        value={options.save_to_db}
                        onChange={updateOption}
                    />

                    <CustomWordsSection
                        customWords={options.custom_words || []}
                        newWord={newCustomWord}
                        setNewWord={setNewCustomWord}
                        onChangeCustomWords={(words) => updateOption('custom_words', words)}
                    />
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2.5, bgcolor: 'background.default', borderTop: '1px solid', borderColor: 'divider' }}>
                <Button onClick={onClose} sx={{ fontWeight: 600, color: 'text.secondary' }}>
                    {t('common.cancel', 'Zrušiť')}
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disableElevation
                    sx={{ borderRadius: 2, px: 3, fontWeight: 600 }}
                >
                    {isEdit ? t('common.save', 'Uložiť') : t('common.create', 'Vytvoriť')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AnonymizationProfileFormDialog;
